import * as React from "react";

// MUI
import {
	withStyles,
	WithStyles,
	createStyles,
	Theme,
} from "@material-ui/core/styles";
import { DatePicker } from "@material-ui/pickers";
import { ParsableDate } from "@material-ui/pickers/constants/prop-types";

// NPM
import clsx from "clsx";
import { animated, useSpring, config } from "react-spring";
import moment from "moment";

// Classes
import Utility from "../../domain/classes/Utility";
import Calendar from "../../domain/classes/Calendar";
import DateTime from "../../domain/classes/DateTime";

// Components
import StyledPromptedButton from "../common/PromptedButton.Component";
import StyledPromptedIconButton from "../common/PromptedIconButton.Component";

// Icons
import DateRangeRoundedIcon from "@material-ui/icons/DateRangeRounded";

// Contexts
import { useCalendarManager } from "../../contexts/CalendarManager.Context";
import IAnimatable from "../../domain/interfaces/IAnimatable";

export const styles = (theme: Theme) =>
	createStyles({
		root: {
			display: "flex",
			justifyContent: "center",
			marginBottom: theme.spacing(2),
		},
		buttonContainer: {
			border: `1px solid ${theme.palette.primary.light}`,
			width: "fit-content",
			padding: theme.spacing(1, 3),
			borderRadius: 23,
		},
		button: { marginRight: theme.spacing(1) },
	});

export interface DateNavigationToolbarProps
	extends React.HTMLAttributes<HTMLDivElement>,
		IAnimatable {}

const DateNavigationToolbar = React.forwardRef<
	HTMLDivElement,
	DateNavigationToolbarProps & WithStyles<typeof styles>
>(({ classes, className, hasAnimation, ...divProps }, ref) => {
	// Contexts
	const {
		handleDateRangeChange,
		rangeSize,
		dateRange,
	} = useCalendarManager();

	// States
	const [date, setDate] = React.useState<ParsableDate>(moment().toDate());
	const [open, setOpen] = React.useState<boolean>(false);

	const handleTodayButtonClick = () => {
		const range = Calendar.generateDateRangeFrom(
			moment().format(DateTime.DEFAULT_DATE_FORMAT),
			rangeSize
		);

		handleDateRangeChange(range);
	};

	const handleDateChange = (date: ParsableDate) => {
		setDate(date);
		const range = Calendar.generateDateRangeFrom(
			moment(date).format(DateTime.DEFAULT_DATE_FORMAT),
			rangeSize
		);
		handleDateRangeChange(range);
	};

	// Spring
	const { opacity, y } = useSpring({
		config: config.wobbly,
		opacity: 1,
		y: 0,
		from: { opacity: 0, y: -20 },
	});

	React.useEffect(() => {
		const date = moment(dateRange[0]).toDate();
		setDate(date);
	}, [dateRange]);

	const content = (
		<div className={classes.buttonContainer}>
			<StyledPromptedButton
				tooltipTitle={`Go to ${moment().format("Do MMM YYYY")}`}
				color="primary"
				size="small"
				className={classes.button}
				onClick={handleTodayButtonClick}
			>
				Today
			</StyledPromptedButton>
			<StyledPromptedIconButton
				tooltipTitle="Open date picker"
				size="small"
				onClick={() => setOpen(true)}
			>
				<DateRangeRoundedIcon color="primary" fontSize="small" />
			</StyledPromptedIconButton>
			<DatePicker
				value={date}
				onChange={handleDateChange}
				open={open}
				onClose={() => setOpen(false)}
				autoOk
				style={{ display: "none" }}
				disablePast
			/>
		</div>
	);

	return hasAnimation ? (
		<animated.div
			{...divProps}
			className={clsx(classes.root, className)}
			ref={ref}
			style={{
				opacity,
				transform: y.interpolate((y) => `translateY(${y}px)`),
			}}
		>
			{content}
		</animated.div>
	) : (
		<div {...divProps} className={clsx(classes.root, className)} ref={ref}>
			{content}
		</div>
	);
});

const StyledDateNavigationToolbar = withStyles(styles, {
	classNamePrefix: Utility.generateClassName("StyledDateNavigationToolbar"),
})(DateNavigationToolbar);

export default StyledDateNavigationToolbar;

export const AnimatedDateNavigationToolbar = animated(
	StyledDateNavigationToolbar
);
